import clsx from 'clsx';
import type { JSX } from 'react';
import type { PublicPropertySummary, PublishedUnitSummary } from '@/lib/types/publicProperty';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { PublicUnitCard } from './PublicUnitCard';

export interface AvailableUnitGroup {
  property: PublicPropertySummary;
  units: PublishedUnitSummary[];
}

export interface PublicUnitGroupListProps {
  groups: AvailableUnitGroup[];
  className?: string;
  emptyFallback?: JSX.Element | null;
  getUnitHref?: (property: PublicPropertySummary, unit: PublishedUnitSummary) => string;
}

export function buildAvailableUnitGroups(
  properties: PublicPropertySummary[],
): AvailableUnitGroup[] {
  return properties
    .map((property) => {
      const units = property.publishedUnits;
      if (units.length === 0) {
        return null;
      }
      return { property, units };
    })
    .filter(Boolean) as AvailableUnitGroup[];
}

export function PublicUnitGroupList({
  groups,
  className,
  emptyFallback,
  getUnitHref = defaultUnitHref,
}: PublicUnitGroupListProps): JSX.Element {
  const siteSettings = useSiteSettingsDetail().data;
  const emptyLabel =
    siteSettings?.publicContent.shared.emptyStates.unitGroupEmpty ??
    'No published units are currently available. Check back soon for new listings.';
  const unitEntries = groups.flatMap((group) =>
    group.units.map((unit) => ({
      property: group.property,
      unit,
    })),
  );

  if (unitEntries.length === 0) {
    return (
      emptyFallback ?? (
        <p className="rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center text-sm text-text-primary/70">
          {emptyLabel}
        </p>
      )
    );
  }

  return (
    <div className={clsx('grid gap-4 sm:grid-cols-2 xl:grid-cols-3', className)}>
      {unitEntries.map(({ property, unit }) => (
        <PublicUnitCard
          key={`${property.id}-${unit.id}`}
          property={property}
          unit={unit}
          href={getUnitHref(property, unit)}
        />
      ))}
    </div>
  );
}

function defaultUnitHref(property: PublicPropertySummary, unit: PublishedUnitSummary): string {
  return `/properties/${property.propertyCode.toLowerCase()}/units/${unit.id}`;
}
